import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  useCallback,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Button,
  Input,
  Radio,
  RadioGroup,
  SearchField,
} from "react-aria-components";

import accountArrowRight from "@/assets/figma/card-menu/account-arrow-right.svg";
import accountDivider from "@/assets/figma/card-menu/account-divider.svg";
import chatNewIcon from "@/assets/figma/card-menu/chat-new.svg";
import chatSearchIcon from "@/assets/figma/card-menu/chat-search.svg";
import currencyCheckedIcon from "@/assets/figma/card-menu/currency-checked.svg";
import currencyUncheckedIcon from "@/assets/figma/card-menu/currency-unchecked.svg";
import languageCheckedIcon from "@/assets/figma/card-menu/language-checked.svg";
import languageUncheckedIcon from "@/assets/figma/card-menu/language-unchecked.svg";
import notificationAbnormalIcon from "@/assets/figma/card-menu/notification-abnormal.svg";
import notificationDotIcon from "@/assets/figma/card-menu/notification-dot.svg";
import notificationInviteIcon from "@/assets/figma/card-menu/notification-invite.svg";
import notificationReadAllIcon from "@/assets/figma/card-menu/notification-read-all.svg";
import notificationSearchIcon from "@/assets/figma/card-menu/notification-search.svg";
import notificationIcon from "@/assets/figma/card-menu/notification.svg";
import notificationWarningIcon from "@/assets/figma/card-menu/notification-warning.svg";
import storeAvatarBaseDanger from "@/assets/figma/card-menu/store-avatar-base-danger.svg";
import storeAvatarBaseDefault from "@/assets/figma/card-menu/store-avatar-base-default.svg";
import storeAvatarBaseSync from "@/assets/figma/card-menu/store-avatar-base-sync.svg";
import storeAvatarBaseTodo from "@/assets/figma/card-menu/store-avatar-base-todo.svg";
import storeCheckedIcon from "@/assets/figma/card-menu/store-checked.svg";
import storeManageIcon from "@/assets/figma/card-menu/store-manage.svg";
import storeUncheckedIcon from "@/assets/figma/card-menu/store-unchecked.svg";
import supportExternalLink from "@/assets/figma/card-menu/support-external-link.svg";
import widgetDivider from "@/assets/figma/card-menu/widget-divider.svg";
import { cn } from "@/shared/lib/cn";
import { MerryChatThreadSummary } from "@/shared/ui/MerryChatThreadSummary";
import type {
  MerryChatActivityItem,
  MerryChatActivityStatus,
} from "@/shared/ui/merryChatActivityModel";
import {
  getMerryCardMenuFigmaNodeId,
  getMerryCardMenuSize,
  type MerryCardMenuView,
  type MerrySystemMenuSubmenuView,
} from "@/shared/ui/merryCardMenuVariants";

gsap.registerPlugin(useGSAP);

export interface MerryCardMenuAction {
  description?: string;
  descriptionStyle?: "multi" | "single";
  iconSource?: string;
  id: string;
  label: string;
  targetView?: MerryCardMenuView;
  trailing?: "arrow" | "external" | "none";
  trailingIconSource?: string;
  value?: string;
}

export interface MerryCardMenuMetric {
  iconSource: string;
  id: string;
  label: string;
  value: string;
}

export interface MerryCardMenuStore {
  avatarSource: string;
  countryCode?: string;
  countryLabel?: string;
  countrySource: string;
  id: string;
  platformBadge?: string;
  platformLabel: string;
  platformSource: string;
  provider?: string;
  statusLabel: string;
  title: string;
  tone: "danger" | "default" | "sync" | "todo";
}

export interface MerryCardMenuStoreGroup {
  id: string;
  label: string;
  stores: ReadonlyArray<MerryCardMenuStore>;
}

export type MerryCardMenuChatStatus = MerryChatActivityStatus;

export type MerryCardMenuChatItem = MerryChatActivityItem;

export interface MerryCardMenuChatSection {
  id: string;
  items: ReadonlyArray<MerryCardMenuChatItem>;
  label: string;
}

export type MerryCardMenuNotificationType =
  | "abnormal"
  | "invite"
  | "notification"
  | "warning";

export interface MerryCardMenuNotificationItem {
  description: string;
  id: string;
  isUnread?: boolean;
  time: string;
  title: string;
  type: MerryCardMenuNotificationType;
}

export interface MerryCardMenuNotificationSection {
  id: string;
  items: ReadonlyArray<MerryCardMenuNotificationItem>;
  label: string;
}

export interface MerryCardMenuOption {
  id: string;
  label: string;
}

export interface MerryCardMenuState {
  action?: MerryCardMenuAction;
  kind: "empty" | "error" | "loading";
  message: string;
}

export interface MerryCardMenuCatalog {
  account: {
    actions: ReadonlyArray<MerryCardMenuAction>;
    billingAction: MerryCardMenuAction;
    displayActions: ReadonlyArray<MerryCardMenuAction>;
    displayTitle: string;
    logoutAction: MerryCardMenuAction;
    metrics: ReadonlyArray<MerryCardMenuMetric>;
    supportAction: MerryCardMenuAction;
    title: string;
  };
  chatHistory: {
    newChatLabel: string;
    searchPlaceholder: string;
    sections: ReadonlyArray<MerryCardMenuChatSection>;
  };
  currency: {
    options: ReadonlyArray<MerryCardMenuOption>;
    title: string;
  };
  language: {
    options: ReadonlyArray<MerryCardMenuOption>;
    title: string;
  };
  notifications: {
    markAllReadLabel: string;
    searchPlaceholder: string;
    sections: ReadonlyArray<MerryCardMenuNotificationSection>;
  };
  store: {
    actions: ReadonlyArray<MerryCardMenuAction>;
    groups: ReadonlyArray<MerryCardMenuStoreGroup>;
    state?: MerryCardMenuState;
    title: string;
  };
  support: {
    items: ReadonlyArray<MerryCardMenuAction>;
    state?: MerryCardMenuState;
    title: string;
  };
  widget: {
    actions: ReadonlyArray<MerryCardMenuAction>;
    removeAction: MerryCardMenuAction;
    title: string;
  };
}

export interface MerryCardMenuProps {
  "aria-label"?: string;
  "data-testid"?: string;
  activeAccountView?: MerrySystemMenuSubmenuView | null;
  animateEntrance?: boolean;
  catalog: MerryCardMenuCatalog;
  className?: string;
  currency?: string;
  defaultCurrency?: string;
  defaultLanguage?: string;
  defaultSelectedStoreId?: string;
  defaultView?: MerryCardMenuView;
  language?: string;
  onAction?: (action: MerryCardMenuAction, view: MerryCardMenuView) => void;
  onCurrencyChange?: (currency: string) => void;
  onLanguageChange?: (language: string) => void;
  onSelectedStoreChange?: (storeId: string) => void;
  onViewChange?: (view: MerryCardMenuView) => void;
  selectedStoreId?: string;
  view?: MerryCardMenuView;
}

const notificationIcons: Record<MerryCardMenuNotificationType, string> = {
  abnormal: notificationAbnormalIcon,
  invite: notificationInviteIcon,
  notification: notificationIcon,
  warning: notificationWarningIcon,
};

const storeAvatarBases: Record<MerryCardMenuStore["tone"], string> = {
  danger: storeAvatarBaseDanger,
  default: storeAvatarBaseDefault,
  sync: storeAvatarBaseSync,
  todo: storeAvatarBaseTodo,
};

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function restoreCardMenuContentVisibility(content: HTMLElement) {
  gsap.set(content, { autoAlpha: 1 });
  gsap.set(content, { clearProps: "opacity,visibility" });
}

function restoreCardMenuSurfaceVisibility(
  root: HTMLElement,
  content?: HTMLElement | null,
  size?: { height: number; width: number },
) {
  gsap.set(root, { autoAlpha: 1 });
  gsap.set(root, {
    clearProps: "opacity,transform,transformOrigin,visibility",
  });
  if (size) {
    gsap.set(root, {
      height: size.height,
      width: size.width,
    });
  }
  if (content) restoreCardMenuContentVisibility(content);
  root.dataset.cardMenuAnimating = "false";
}

function MenuButton({
  children,
  className,
  onPress,
  testId,
}: {
  children: ReactNode;
  className?: string;
  onPress?: () => void;
  testId?: string;
}) {
  return (
    <Button
      className={({ isFocusVisible, isHovered, isPressed }) =>
        cn(
          "merry-card-menu__button",
          isHovered && "merry-card-menu__button--hovered",
          isPressed && "merry-card-menu__button--pressed",
          isFocusVisible && "merry-card-menu__button--focus-visible",
          className,
        )
      }
      data-testid={testId}
      onPress={onPress}
      type="button"
    >
      {children}
    </Button>
  );
}

function MenuTitle({ children }: { children: ReactNode }) {
  return <div className="merry-card-menu__title">{children}</div>;
}

function SimpleActionRow({
  action,
  className,
  onPress,
}: {
  action: MerryCardMenuAction;
  className?: string;
  onPress: () => void;
}) {
  const trailing = action.trailing ?? (action.targetView ? "arrow" : "none");

  return (
    <MenuButton
      className={cn(
        "merry-card-menu__row",
        action.description && "merry-card-menu__row--rich",
        action.descriptionStyle === "multi" &&
          "merry-card-menu__row--multiline",
        className,
      )}
      onPress={onPress}
      testId={`card-menu-action-${action.id}`}
    >
      <span className="merry-card-menu__row-main">
        {action.iconSource ? (
          <img
            alt=""
            aria-hidden="true"
            className="merry-card-menu__row-icon"
            src={action.iconSource}
          />
        ) : null}
        <span className="merry-card-menu__row-copy">
          <strong>{action.label}</strong>
          {action.description ? <span>{action.description}</span> : null}
        </span>
      </span>
      {action.value || trailing !== "none" ? (
        <span className="merry-card-menu__row-trailing">
          {action.value ? (
            <span className="merry-card-menu__row-value">{action.value}</span>
          ) : null}
          {trailing === "arrow" ? (
            <img
              alt=""
              aria-hidden="true"
              className="merry-card-menu__row-arrow"
              src={action.trailingIconSource ?? accountArrowRight}
            />
          ) : null}
          {trailing === "external" ? (
            <img
              alt=""
              aria-hidden="true"
              className="merry-card-menu__row-external"
              src={supportExternalLink}
            />
          ) : null}
        </span>
      ) : null}
    </MenuButton>
  );
}

function AccountPanel({
  activeView,
  catalog,
  currencyLabel,
  languageLabel,
  onAction,
}: {
  activeView?: MerrySystemMenuSubmenuView | null;
  catalog: MerryCardMenuCatalog["account"];
  currencyLabel: string;
  languageLabel: string;
  onAction: (action: MerryCardMenuAction) => void;
}) {
  return (
    <div className="merry-card-menu__scroll merry-card-menu__account">
      <section className="merry-card-menu__section">
        <MenuTitle>{catalog.title}</MenuTitle>
        {catalog.actions.map((action) => (
          <SimpleActionRow
            action={action}
            key={action.id}
            onPress={() => onAction(action)}
          />
        ))}
        <div className="merry-card-menu__account-billing">
          <SimpleActionRow
            action={catalog.billingAction}
            onPress={() => onAction(catalog.billingAction)}
          />
          {catalog.metrics.map((metric) => (
            <div className="merry-card-menu__row" key={metric.id}>
              <span className="merry-card-menu__row-main">
                <img
                  alt=""
                  aria-hidden="true"
                  className="merry-card-menu__row-icon"
                  src={metric.iconSource}
                />
                <span className="merry-card-menu__row-copy">
                  <span>{metric.label}</span>
                </span>
              </span>
              <span className="merry-card-menu__row-trailing">
                <span className="merry-card-menu__row-value">
                  {metric.value}
                </span>
              </span>
            </div>
          ))}
        </div>
        <SimpleActionRow
          action={catalog.supportAction}
          className={cn(
            activeView === "support" && "merry-card-menu__row--active",
          )}
          onPress={() => onAction(catalog.supportAction)}
        />
      </section>

      <section className="merry-card-menu__section">
        <MenuTitle>{catalog.displayTitle}</MenuTitle>
        {catalog.displayActions.map((action) => {
          const value =
            action.targetView === "currency"
              ? currencyLabel
              : action.targetView === "language"
                ? languageLabel
                : action.value;
          const renderedAction = { ...action, value };

          return (
            <SimpleActionRow
              action={renderedAction}
              className={cn(
                activeView === action.targetView &&
                  "merry-card-menu__row--active",
              )}
              key={action.id}
              onPress={() => onAction(action)}
            />
          );
        })}
      </section>

      <div className="merry-card-menu__account-footer">
        <span className="merry-card-menu__divider" aria-hidden="true">
          <img alt="" src={accountDivider} />
        </span>
        <SimpleActionRow
          action={catalog.logoutAction}
          onPress={() => onAction(catalog.logoutAction)}
        />
      </div>
    </div>
  );
}

function StoreAvatar({ store }: { store: MerryCardMenuStore }) {
  return (
    <span className="merry-card-menu__store-avatar" aria-hidden="true">
      <img
        alt=""
        className="merry-card-menu__store-avatar-base"
        src={storeAvatarBases[store.tone]}
      />
      <img
        alt=""
        className="merry-card-menu__store-avatar-image"
        src={store.avatarSource}
      />
    </span>
  );
}

function StorePanel({
  catalog,
  onAction,
  onSelectedStoreChange,
  selectedStoreId,
}: {
  catalog: MerryCardMenuCatalog["store"];
  onAction: (action: MerryCardMenuAction) => void;
  onSelectedStoreChange: (storeId: string) => void;
  selectedStoreId: string;
}) {
  const stateAction = catalog.state?.action;

  return (
    <div className="merry-card-menu__scroll merry-card-menu__store">
      <section className="merry-card-menu__section">
        <MenuTitle>{catalog.title}</MenuTitle>
        {catalog.actions.map((action) => (
          <SimpleActionRow
            action={action}
            key={action.id}
            onPress={() => onAction(action)}
          />
        ))}
      </section>

      {catalog.state ? (
        <div
          className="merry-card-menu__support-state merry-card-menu__store-state"
          role={catalog.state.kind === "error" ? "alert" : "status"}
        >
          <p>{catalog.state.message}</p>
          {stateAction ? (
            <SimpleActionRow
              action={stateAction}
              onPress={() => onAction(stateAction)}
            />
          ) : null}
        </div>
      ) : (
        <RadioGroup
          aria-label="选择店铺"
          className="merry-card-menu__store-groups"
          onChange={onSelectedStoreChange}
          value={selectedStoreId}
        >
          {catalog.groups.map((group) => (
            <section className="merry-card-menu__section" key={group.id}>
              <MenuTitle>{group.label}</MenuTitle>
              {group.stores.map((store) => (
                <Radio
                  className={({
                    isFocusVisible,
                    isHovered,
                    isPressed,
                    isSelected,
                  }) =>
                    cn(
                      "merry-card-menu__button merry-card-menu__store-row",
                      isSelected && "merry-card-menu__store-row--selected",
                      isHovered && "merry-card-menu__button--hovered",
                      isPressed && "merry-card-menu__button--pressed",
                      isFocusVisible &&
                        "merry-card-menu__button--focus-visible",
                    )
                  }
                  data-testid={`card-menu-store-${store.id}`}
                  key={store.id}
                  value={store.id}
                >
                  {({ isSelected }) => (
                    <>
                      <StoreAvatar store={store} />
                      <span className="merry-card-menu__store-copy">
                        <span className="merry-card-menu__store-title">
                          <img alt="" aria-hidden="true" src={storeManageIcon} />
                          <strong>{store.title}</strong>
                        </span>
                        <span className="merry-card-menu__store-platform">
                          <span aria-hidden="true" data-store-identity={store.countryCode || store.platformBadge ? "dynamic" : undefined}>
                            {store.countryCode ? (
                              <span className={`flag:${store.countryCode} merry-card-menu__country-flag`} />
                            ) : store.countrySource ? (
                              <img alt="" src={store.countrySource} />
                            ) : null}
                            {store.platformSource ? (
                              <img alt="" src={store.platformSource} />
                            ) : store.platformBadge ? (
                              <span className="merry-card-menu__platform-badge" data-provider={store.provider}>{store.platformBadge}</span>
                            ) : null}
                          </span>
                          <span className="merry-card-menu__store-identity-label">
                            {store.platformLabel}{store.countryLabel ? ` · ${store.countryLabel}` : null}
                          </span>
                        </span>
                        <span className="merry-card-menu__store-status-label">
                          {store.statusLabel}
                        </span>
                      </span>
                      <img
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          "merry-card-menu__choice-icon",
                          !isSelected &&
                            "merry-card-menu__choice-icon--unchecked",
                        )}
                        src={isSelected ? storeCheckedIcon : storeUncheckedIcon}
                      />
                    </>
                  )}
                </Radio>
              ))}
            </section>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}

function MenuToolbar({
  actionIcon,
  actionLabel,
  onAction,
  onSearchChange,
  placeholder,
  searchIcon,
}: {
  actionIcon: string;
  actionLabel: string;
  onAction: () => void;
  onSearchChange: (value: string) => void;
  placeholder: string;
  searchIcon: string;
}) {
  return (
    <div className="merry-card-menu__toolbar">
      <SearchField
        aria-label={placeholder}
        className="merry-card-menu__search"
        onChange={onSearchChange}
      >
        <img alt="" aria-hidden="true" src={searchIcon} />
        <Input placeholder={placeholder} />
      </SearchField>
      <MenuButton className="merry-card-menu__toolbar-action" onPress={onAction}>
        <img alt="" aria-hidden="true" src={actionIcon} />
        <span>{actionLabel}</span>
      </MenuButton>
    </div>
  );
}

function ChatHistoryPanel({
  catalog,
  onAction,
}: {
  catalog: MerryCardMenuCatalog["chatHistory"];
  onAction: (action: MerryCardMenuAction) => void;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");

  return (
    <div className="merry-card-menu__scroll merry-card-menu__history">
      <MenuToolbar
        actionIcon={chatNewIcon}
        actionLabel={catalog.newChatLabel}
        onAction={() =>
          onAction({ id: "new-chat", label: catalog.newChatLabel })
        }
        onSearchChange={setQuery}
        placeholder={catalog.searchPlaceholder}
        searchIcon={chatSearchIcon}
      />
      {catalog.sections.map((section) => {
        const items = section.items.filter((item) =>
          item.title.toLocaleLowerCase("zh-CN").includes(normalizedQuery),
        );
        if (items.length === 0) return null;

        return (
          <section className="merry-card-menu__section" key={section.id}>
            <MenuTitle>{section.label}</MenuTitle>
            {items.map((item) => (
              <MenuButton
                className="merry-card-menu__history-row"
                key={item.id}
                onPress={() =>
                  onAction({ id: item.id, label: item.title })
                }
                testId={`card-menu-chat-${item.id}`}
              >
                <MerryChatThreadSummary item={item} />
              </MenuButton>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function NotificationsPanel({
  catalog,
  onAction,
}: {
  catalog: MerryCardMenuCatalog["notifications"];
  onAction: (action: MerryCardMenuAction) => void;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");

  return (
    <div className="merry-card-menu__scroll merry-card-menu__notifications">
      <MenuToolbar
        actionIcon={notificationReadAllIcon}
        actionLabel={catalog.markAllReadLabel}
        onAction={() =>
          onAction({ id: "mark-all-read", label: catalog.markAllReadLabel })
        }
        onSearchChange={setQuery}
        placeholder={catalog.searchPlaceholder}
        searchIcon={notificationSearchIcon}
      />
      {catalog.sections.map((section) => {
        const items = section.items.filter((item) =>
          `${item.title}${item.description}`
            .toLocaleLowerCase("zh-CN")
            .includes(normalizedQuery),
        );
        if (items.length === 0) return null;

        return (
          <section className="merry-card-menu__section" key={section.id}>
            <MenuTitle>{section.label}</MenuTitle>
            {items.map((item) => (
              <MenuButton
                className="merry-card-menu__notification-row"
                key={item.id}
                onPress={() =>
                  onAction({ id: item.id, label: item.title })
                }
                testId={`card-menu-notification-${item.id}`}
              >
                <span className="merry-card-menu__notification-title">
                  <img
                    alt=""
                    aria-hidden="true"
                    className="merry-card-menu__notification-icon"
                    src={notificationIcons[item.type]}
                  />
                  <strong>{item.title}</strong>
                </span>
                <span className="merry-card-menu__notification-description">
                  {item.description}
                </span>
                <span className="merry-card-menu__notification-meta">
                  <time>{item.time}</time>
                  {item.isUnread ? (
                    <img alt="" aria-hidden="true" src={notificationDotIcon} />
                  ) : null}
                </span>
              </MenuButton>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ChoicePanel({
  checkedIcon,
  onChange,
  options,
  title,
  uncheckedIcon,
  value,
}: {
  checkedIcon: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<MerryCardMenuOption>;
  title: string;
  uncheckedIcon: string;
  value: string;
}) {
  return (
    <RadioGroup
      aria-label={title}
      className="merry-card-menu__choices"
      onChange={onChange}
      value={value}
    >
      <MenuTitle>{title}</MenuTitle>
      {options.map((option) => (
        <Radio
          className={({
            isFocusVisible,
            isHovered,
            isPressed,
            isSelected,
          }) =>
            cn(
              "merry-card-menu__button merry-card-menu__choice-row",
              isSelected && "merry-card-menu__choice-row--selected",
              isHovered && "merry-card-menu__button--hovered",
              isPressed && "merry-card-menu__button--pressed",
              isFocusVisible && "merry-card-menu__button--focus-visible",
            )
          }
          data-testid={`card-menu-choice-${option.id}`}
          key={option.id}
          value={option.id}
        >
          {({ isSelected }) => (
            <>
              <strong>{option.label}</strong>
              <img
                alt=""
                aria-hidden="true"
                className={cn(
                  "merry-card-menu__choice-icon",
                  !isSelected &&
                    "merry-card-menu__choice-icon--unchecked",
                )}
                src={isSelected ? checkedIcon : uncheckedIcon}
              />
            </>
          )}
        </Radio>
      ))}
    </RadioGroup>
  );
}

function SupportPanel({
  catalog,
  onAction,
}: {
  catalog: MerryCardMenuCatalog["support"];
  onAction: (action: MerryCardMenuAction) => void;
}) {
  const stateAction = catalog.state?.action;

  return (
    <div className="merry-card-menu__scroll merry-card-menu__support">
      <MenuTitle>{catalog.title}</MenuTitle>
      {catalog.state ? (
        <div
          className="merry-card-menu__support-state"
          role={catalog.state.kind === "error" ? "alert" : "status"}
        >
          <p>{catalog.state.message}</p>
          {stateAction ? (
            <SimpleActionRow
              action={stateAction}
              onPress={() => onAction(stateAction)}
            />
          ) : null}
        </div>
      ) : (
        catalog.items.map((action) => (
          <SimpleActionRow
            action={action}
            key={action.id}
            onPress={() => onAction(action)}
          />
        ))
      )}
    </div>
  );
}

function WidgetPanel({
  catalog,
  onAction,
}: {
  catalog: MerryCardMenuCatalog["widget"];
  onAction: (action: MerryCardMenuAction) => void;
}) {
  return (
    <div className="merry-card-menu__widget">
      <MenuTitle>{catalog.title}</MenuTitle>
      {catalog.actions.map((action) => (
        <SimpleActionRow
          action={action}
          key={action.id}
          onPress={() => onAction(action)}
        />
      ))}
      <span className="merry-card-menu__divider" aria-hidden="true">
        <img alt="" src={widgetDivider} />
      </span>
      <SimpleActionRow
        action={catalog.removeAction}
        onPress={() => onAction(catalog.removeAction)}
      />
    </div>
  );
}

export function MerryCardMenu({
  "aria-label": ariaLabel = "功能菜单",
  "data-testid": testId,
  activeAccountView = null,
  animateEntrance = true,
  catalog,
  className,
  currency,
  defaultCurrency = catalog.currency.options[0]?.id ?? "",
  defaultLanguage = catalog.language.options[0]?.id ?? "",
  defaultSelectedStoreId = catalog.store.groups[0]?.stores[0]?.id ?? "",
  defaultView = "account",
  language,
  onAction,
  onCurrencyChange,
  onLanguageChange,
  onSelectedStoreChange,
  onViewChange,
  selectedStoreId,
  view,
}: MerryCardMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isInitialMotionRef = useRef(true);
  const isInitialContentMotionRef = useRef(true);
  const [initialSize] = useState(() =>
    getMerryCardMenuSize(view ?? defaultView),
  );
  const [internalView, setInternalView] = useState(defaultView);
  const [renderedView, setRenderedView] = useState(view ?? defaultView);
  const [internalStoreId, setInternalStoreId] = useState(
    defaultSelectedStoreId,
  );
  const [internalCurrency, setInternalCurrency] = useState(defaultCurrency);
  const [internalLanguage, setInternalLanguage] = useState(defaultLanguage);
  const requestedView = view ?? internalView;
  const requestedSize = getMerryCardMenuSize(requestedView);
  const displayedViewRef = useRef(requestedView);
  const activeStoreId = selectedStoreId ?? internalStoreId;
  const activeCurrency = currency ?? internalCurrency;
  const activeLanguage = language ?? internalLanguage;
  const activeCurrencyLabel =
    catalog.currency.options.find((option) => option.id === activeCurrency)
      ?.label ?? activeCurrency;
  const activeLanguageLabel =
    catalog.language.options.find((option) => option.id === activeLanguage)
      ?.label ?? activeLanguage;

  const changeView = useCallback(
    (nextView: MerryCardMenuView) => {
      if (view === undefined) setInternalView(nextView);
      onViewChange?.(nextView);
    },
    [onViewChange, view],
  );

  const handleAction = useCallback(
    (action: MerryCardMenuAction) => {
      onAction?.(action, requestedView);
      if (action.targetView) changeView(action.targetView);
    },
    [changeView, onAction, requestedView],
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;
      if (!root || !content) return;

      gsap.killTweensOf([root, content]);

      if (isInitialMotionRef.current) {
        isInitialMotionRef.current = false;
        displayedViewRef.current = requestedView;
        if (!animateEntrance || prefersReducedMotion()) {
          restoreCardMenuSurfaceVisibility(root, content, requestedSize);
          return;
        }

        root.dataset.cardMenuAnimating = "true";
        gsap.fromTo(
          root,
          {
            autoAlpha: 0,
            scale: 0.985,
            transformOrigin: "top right",
            y: -6,
          },
          {
            autoAlpha: 1,
            duration: 0.22,
            ease: "power3.out",
            scale: 1,
            y: 0,
            onComplete: () =>
              restoreCardMenuSurfaceVisibility(root, content),
            onInterrupt: () =>
              restoreCardMenuSurfaceVisibility(root, content),
          },
        );
        return;
      }

      if (requestedView === displayedViewRef.current) {
        restoreCardMenuSurfaceVisibility(root, content, requestedSize);
        return;
      }

      if (prefersReducedMotion()) {
        displayedViewRef.current = requestedView;
        setRenderedView(requestedView);
        restoreCardMenuSurfaceVisibility(root, content, requestedSize);
        return;
      }

      root.dataset.cardMenuAnimating = "true";
      gsap.to(root, {
        duration: 0.32,
        ease: "power3.inOut",
        height: requestedSize.height,
        overwrite: true,
        width: requestedSize.width,
        onComplete: () => {
          root.dataset.cardMenuAnimating = "false";
        },
        onInterrupt: () => restoreCardMenuSurfaceVisibility(root, content),
      });
      gsap.to(content, {
        autoAlpha: 0,
        duration: 0.14,
        ease: "power2.in",
        overwrite: true,
        onComplete: () => {
          displayedViewRef.current = requestedView;
          setRenderedView(requestedView);
        },
        onInterrupt: () => restoreCardMenuContentVisibility(content),
      });
    },
    { dependencies: [animateEntrance, requestedView], scope: rootRef },
  );

  useGSAP(
    () => {
      const content = contentRef.current;
      if (!content) return;

      if (isInitialContentMotionRef.current) {
        isInitialContentMotionRef.current = false;
        return;
      }

      if (prefersReducedMotion()) {
        restoreCardMenuContentVisibility(content);
        return;
      }

      gsap.fromTo(
        content,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.18,
          ease: "power2.out",
          overwrite: true,
          onComplete: () => restoreCardMenuContentVisibility(content),
          onInterrupt: () => restoreCardMenuContentVisibility(content),
        },
      );
    },
    { dependencies: [renderedView], scope: rootRef },
  );

  const updateStore = (storeId: string) => {
    if (selectedStoreId === undefined) setInternalStoreId(storeId);
    onSelectedStoreChange?.(storeId);
  };

  const updateCurrency = (nextCurrency: string) => {
    if (currency === undefined) setInternalCurrency(nextCurrency);
    onCurrencyChange?.(nextCurrency);
  };

  const updateLanguage = (nextLanguage: string) => {
    if (language === undefined) setInternalLanguage(nextLanguage);
    onLanguageChange?.(nextLanguage);
  };

  return (
    <div
      aria-label={ariaLabel}
      className={cn("merry-card-menu", className)}
      data-card-menu-view={requestedView}
      data-figma-component-id="300:35698"
      data-figma-node-id={getMerryCardMenuFigmaNodeId(requestedView)}
      data-testid={testId}
      ref={rootRef}
      role="group"
      style={{ height: initialSize.height, width: initialSize.width }}
    >
      <span aria-hidden="true" className="merry-card-menu__glass" />
      <div className="merry-card-menu__content" ref={contentRef}>
        {renderedView === "account" ? (
          <AccountPanel
            activeView={activeAccountView}
            catalog={catalog.account}
            currencyLabel={activeCurrencyLabel}
            languageLabel={activeLanguageLabel}
            onAction={handleAction}
          />
        ) : null}
        {renderedView === "store" ? (
          <StorePanel
            catalog={catalog.store}
            onAction={handleAction}
            onSelectedStoreChange={updateStore}
            selectedStoreId={activeStoreId}
          />
        ) : null}
        {renderedView === "chat-history" ? (
          <ChatHistoryPanel
            catalog={catalog.chatHistory}
            onAction={handleAction}
          />
        ) : null}
        {renderedView === "notifications" ? (
          <NotificationsPanel
            catalog={catalog.notifications}
            onAction={handleAction}
          />
        ) : null}
        {renderedView === "currency" ? (
          <ChoicePanel
            checkedIcon={currencyCheckedIcon}
            onChange={updateCurrency}
            options={catalog.currency.options}
            title={catalog.currency.title}
            uncheckedIcon={currencyUncheckedIcon}
            value={activeCurrency}
          />
        ) : null}
        {renderedView === "language" ? (
          <ChoicePanel
            checkedIcon={languageCheckedIcon}
            onChange={updateLanguage}
            options={catalog.language.options}
            title={catalog.language.title}
            uncheckedIcon={languageUncheckedIcon}
            value={activeLanguage}
          />
        ) : null}
        {renderedView === "support" ? (
          <SupportPanel catalog={catalog.support} onAction={handleAction} />
        ) : null}
        {renderedView === "widget" ? (
          <WidgetPanel catalog={catalog.widget} onAction={handleAction} />
        ) : null}
      </div>
      <span aria-hidden="true" className="merry-card-menu__stroke" />
    </div>
  );
}
